import { useEffect, useState } from 'react'
import AdminShell from './AdminShell'
import Toggle from '../../components/Toggle'
import { tournamentApi } from '../../services/api'

/**
 * Fee settings — global defaults + per-reseller overrides.
 *
 * Each platform_fee_settings row is shown as a card with:
 *   - Inline-editable PercentValue (or FlatValue, future)
 *   - List of overrides nested below the card with delete buttons
 *   - "Add override" form per fee that picks a reseller account
 */
export default function FeeSettings() {
  const [fees, setFees] = useState([])
  const [accounts, setAccounts] = useState([])
  const [pkgFeeResellers, setPkgFeeResellers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [savingFeeKey, setSavingFeeKey] = useState(null)

  function reload() {
    setLoading(true)
    Promise.all([
      tournamentApi.get('/admin/fee-settings'),
      tournamentApi.get('/admin/resellers/accounts'),
      tournamentApi.get('/admin/fee-settings/package-fee-overrides'),
    ])
      .then(([f, a, pf]) => {
        setFees(f.data.fees || [])
        setAccounts(a.data.accounts || [])
        setPkgFeeResellers(pf.data.resellers || [])
      })
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }
  useEffect(reload, [])

  async function savePackageFeeOverride(resellerAccountId, patch) {
    // Optimistic local update so the toggle/split feel instant.
    setPkgFeeResellers(prev => prev.map(r => r.resellerAccountId === resellerAccountId ? { ...r, ...patch } : r))
    try {
      const { data } = await tournamentApi.put(`/admin/fee-settings/package-fee-overrides/${resellerAccountId}`, patch)
      setPkgFeeResellers(prev => prev.map(r => r.resellerAccountId === resellerAccountId
        ? { ...r, canOverride: data.canOverride, integolfPct: data.integolfPct, resellerPct: data.resellerPct } : r))
    } catch (err) {
      alert(err?.response?.data?.message || err.message)
      reload()
    }
  }

  async function saveFee(feeKey, patch) {
    setSavingFeeKey(feeKey)
    try {
      await tournamentApi.put(`/admin/fee-settings/${feeKey}`, patch)
      reload()
    } catch (err) {
      alert(err?.response?.data?.message || err.message)
    } finally { setSavingFeeKey(null) }
  }

  async function deleteOverride(feeKey, resellerAccountId) {
    if (!confirm('Remove this override?  The reseller will revert to the global default.')) return
    try {
      await tournamentApi.delete(`/admin/fee-settings/${feeKey}/overrides/${resellerAccountId}`)
      reload()
    } catch (err) { alert(err?.response?.data?.message || err.message) }
  }

  async function addOverride(feeKey, resellerAccountId, percentValue, note) {
    try {
      await tournamentApi.post(`/admin/fee-settings/${feeKey}/overrides`, {
        resellerAccountId: Number(resellerAccountId),
        percentValue: Number(percentValue),
        note: note || null,
      })
      reload()
    } catch (err) { alert(err?.response?.data?.message || err.message) }
  }

  return (
    <AdminShell title="Fee Settings">
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: 24, fontSize: 14, maxWidth: 720 }}>
        Global default rates applied to all resellers. Per-reseller overrides (volume deals, founder pricing, etc.)
        are managed on each reseller's <a href="/admin/reseller-accounts" style={{ color: '#00d19f', fontWeight: 600 }}>account detail page</a>.
        Editing a default here changes the rate for every reseller who doesn't have an override.
      </p>
      {error && <div style={errorBox}>{error}</div>}
      {loading ? <div style={empty}>Loading…</div> : fees.map(f => (
        <FeeCard
          key={f.FeeKey}
          fee={f}
          accounts={accounts}
          saving={savingFeeKey === f.FeeKey}
          onSave={(patch) => saveFee(f.FeeKey, patch)}
          onAddOverride={(rid, pv, n) => addOverride(f.FeeKey, rid, pv, n)}
          onDeleteOverride={(rid) => deleteOverride(f.FeeKey, rid)}
        />
      ))}

      {!loading && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 8, marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ padding: 20, borderBottom: '1px solid #f1f5f9' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#0f172a' }}>Custom package fees (per reseller)</h3>
            <p style={{ margin: '8px 0 0', fontSize: 13, color: '#475569', lineHeight: 1.5, maxWidth: 760 }}>
              When enabled, a reseller can replace the standard platform fee on each package with their own
              percent or flat amount. Set InteGolf's share of any override they create — the reseller keeps the
              remainder. (Display-only estimate for now; real settlement is unchanged.)
            </p>
          </div>
          <div style={{ padding: '4px 8px' }}>
            <table style={tbl}>
              <thead>
                <tr style={tr}>
                  <th style={th}>Reseller</th>
                  <th style={{ ...th, textAlign: 'center' }}>Can override</th>
                  <th style={{ ...th, textAlign: 'right' }}>InteGolf %</th>
                  <th style={{ ...th, textAlign: 'right' }}>Reseller %</th>
                </tr>
              </thead>
              <tbody>
                {pkgFeeResellers.map(r => (
                  <PackageFeeOverrideRow key={r.resellerAccountId} r={r} onSave={patch => savePackageFeeOverride(r.resellerAccountId, patch)} />
                ))}
                {pkgFeeResellers.length === 0 && (
                  <tr><td style={{ ...td, color: '#94a3b8' }} colSpan={4}>No resellers found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminShell>
  )
}

/**
 * One reseller row in the package-fee-override table: an enable toggle plus the
 * InteGolf/Reseller split (two inputs that always sum to 100 — editing InteGolf
 * derives the reseller share). Split saves on blur; toggle saves immediately.
 */
function PackageFeeOverrideRow({ r, onSave }) {
  const [igPct, setIgPct] = useState(String(r.integolfPct))
  useEffect(() => { setIgPct(String(r.integolfPct)) }, [r.integolfPct])
  const igNum = Math.max(0, Math.min(100, Number(igPct) || 0))
  const rsNum = 100 - igNum

  function commitSplit() {
    if (Number(igPct) === r.integolfPct) return
    onSave({ integolfPct: igNum })
  }
  return (
    <tr style={tr}>
      <td style={td}>
        <div>
          <span style={{ fontWeight: 600, color: '#0f172a' }}>{r.name}</span>
          {r.slug && <code style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8' }}>{r.slug}</code>}
        </div>
        {r.email && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{r.email}</div>}
      </td>
      <td style={{ ...td, textAlign: 'center' }}>
        <div style={{ display: 'inline-flex' }}>
          <Toggle checked={r.canOverride} onChange={(v) => onSave({ canOverride: v })} />
        </div>
      </td>
      <td style={{ ...td, textAlign: 'right' }}>
        <input
          type="number" min={0} max={100} step="1" value={igPct}
          disabled={!r.canOverride}
          onChange={e => setIgPct(e.target.value)}
          onBlur={commitSplit}
          style={{ width: 72, padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, textAlign: 'right', opacity: r.canOverride ? 1 : 0.5 }}
        />
        <span style={{ color: '#64748b', marginLeft: 4 }}>%</span>
      </td>
      <td style={{ ...td, textAlign: 'right', color: '#64748b' }}>{rsNum}%</td>
    </tr>
  )
}

function FeeCard({ fee, accounts, saving, onSave, onAddOverride, onDeleteOverride }) {
  // Display percent as e.g. "3.49" not "0.0349"
  const initialPct = fee.PercentValue == null ? '' : (Number(fee.PercentValue) * 100).toFixed(4).replace(/\.?0+$/, '')
  const initialRoundUp = Boolean(fee.RoundUpDollar)
  const [pct, setPct] = useState(initialPct)
  const [roundUp, setRoundUp] = useState(initialRoundUp)
  const [showAdd, setShowAdd] = useState(false)
  const [newResId, setNewResId] = useState('')
  const [newPct, setNewPct] = useState('')
  const [newNote, setNewNote] = useState('')

  const dirty = pct !== initialPct || roundUp !== initialRoundUp

  // Live preview: $107.99 example so the admin sees what the toggle does.
  const exampleBase = 107.99
  const examplePct = Number(pct) / 100 || 0
  const exampleAfterMarkup = exampleBase * (1 + examplePct)
  const exampleFinal = roundUp ? Math.ceil(exampleAfterMarkup) : Number(exampleAfterMarkup.toFixed(2))

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 20, overflow: 'hidden' }}>
      <div style={{ padding: 20, borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#0f172a' }}>{fee.Label}</h3>
            <code style={{ fontSize: 11, color: '#64748b' }}>{fee.FeeKey}</code>
            {fee.Description && <p style={{ margin: '8px 0 0', fontSize: 13, color: '#475569', lineHeight: 1.5 }}>{fee.Description}</p>}

            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Toggle checked={roundUp} onChange={setRoundUp} />
              <span style={{ fontSize: 13, color: '#0f172a', cursor: 'pointer' }} onClick={() => setRoundUp(v => !v)}>
                Round up to nearest dollar after applying this fee
              </span>
              {Number(pct) > 0 && (
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  e.g. ${exampleBase.toFixed(2)} → <b>${exampleFinal.toFixed(2)}</b>
                  {roundUp && (
                    <span style={{ color: '#94a3b8' }}> {' '}(was ${exampleAfterMarkup.toFixed(2)})</span>
                  )}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="number" step="0.0001" min={0} value={pct} onChange={e => setPct(e.target.value)}
              style={{ width: 100, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', textAlign: 'right' }}
            />
            <span style={{ color: '#64748b', fontSize: 14 }}>%</span>
            <button
              onClick={() => onSave({ percentValue: Number(pct) / 100, roundUpDollar: roundUp })}
              disabled={saving || !dirty}
              style={{ ...primaryBtn, marginLeft: 8, opacity: (saving || !dirty) ? 0.5 : 1 }}
            >{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>

      {/* Overrides summary — read-only count + link out, since per-reseller
          editing now lives on each reseller's account detail page. */}
      {fee.overrides.length > 0 && (
        <div style={{ padding: '12px 20px', background: '#f8fafc', fontSize: 13, color: '#475569' }}>
          <strong>{fee.overrides.length}</strong> reseller{fee.overrides.length === 1 ? '' : 's'} with custom rate:{' '}
          {fee.overrides.map((o, i) => (
            <span key={o.ID_NO}>
              {i > 0 && ', '}
              <a href={`/admin/reseller-accounts/${o.ResellerAccount_ID}`} style={{ color: '#0f172a', fontWeight: 600 }}>
                {o.ResellerName}
              </a> <span style={{ color: '#64748b' }}>({(Number(o.PercentValue) * 100).toFixed(4).replace(/\.?0+$/, '')}%)</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 14, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6 }
const tr = { borderBottom: '1px solid #f1f5f9' }
const th = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }
const td = { padding: '12px' }
const tdMono = { ...td, fontFamily: 'monospace' }
const primaryBtn = { padding: '8px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '6px 12px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
const smLabel = { display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }
const inputSm = { padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }
